// src/api/index.js — axios instance that attaches auth token to every request.
// Supports both OTP sessions (otp_token in localStorage) and Entra ID (MSAL).
// Place in: reporting-frontend/src/api/index.js

import axios from 'axios';
import { msalInstance } from '../msalInstance';
import { loginRequest } from '../authConfig';

const api = axios.create({
  baseURL: '',
  timeout: 30000,
});

// Attach Bearer token to every request.
// Checks for an OTP session first (external users), then falls back to Entra ID (internal).
api.interceptors.request.use(async (config) => {

  // ── 1. OTP session (external users) ──────────────────────────────────────
  const otpToken = localStorage.getItem('otp_token');
  if (otpToken) {
    config.headers['Authorization'] = `Bearer ${otpToken}`;
    return config;
  }

  // ── 2. Entra ID (@starlims.com users) ────────────────────────────────────
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return config;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    config.headers['Authorization'] = `Bearer ${response.idToken}`;
  } catch (e) {
    // Silent refresh failed — clear any stale OTP token and redirect to login
    localStorage.removeItem('otp_token');
    msalInstance.acquireTokenRedirect(loginRequest);
  }

  return config;
});

// ── Customers ──────────────────────────────────────────────────────────────────
export const getCustomers    = (activeOnly = false) =>
  api.get(`/api/v1/customers${activeOnly ? '?active_only=true' : ''}`);
export const getCustomer     = (code)              => api.get(`/api/v1/customers/${code}`);
export const createCustomer  = (body)              => api.post('/api/v1/customers', body);
export const updateCustomer  = (code, body)        => api.patch(`/api/v1/customers/${code}`, body);

// ── Usage ──────────────────────────────────────────────────────────────────────
export const getUsage        = (code, dimension)   => api.get(`/api/v1/usage/${code}?dimension=${dimension}`);
export const getUsageSummary = (dimension)         => api.get(`/api/v1/usage?dimension=${dimension}`);

// ── Bundles ────────────────────────────────────────────────────────────────────
export const getBundles      = ()                  => api.get('/api/v1/bundles');
export const createBundle    = (body)              => api.post('/api/v1/bundles', body);
export const updateBundle    = (id, body)          => api.patch(`/api/v1/bundles/${id}`, body);

// ── Allocations ────────────────────────────────────────────────────────────────
export const getAllocations        = (code)        => api.get(`/api/v1/allocations/${code}`);
export const getActiveAllocations  = (code)        => api.get(`/api/v1/allocations/${code}/active`);
export const assignBundle          = (code, body)  => api.post(`/api/v1/allocations/${code}/bundle`, body);
export const addTopup              = (code, body)  => api.post(`/api/v1/allocations/${code}/topup`, body);
export const closeAllocation       = (id, body)    => api.patch(`/api/v1/allocations/${id}/close`, body);

// ── Burn-down ──────────────────────────────────────────────────────────────────
export const getBurndown        = ()               => api.get('/api/v1/burndown');
export const getBurndownHistory = (code)           => api.get(`/api/v1/burndown/${code}/history`);

// ── Sessions / Volumes / DB sizes / ETL ───────────────────────────────────────
export const getSessions     = (code, dimension)   => api.get(`/api/v1/sessions/${code}?dimension=${dimension}`);
export const getSessionsSummary = (dimension)      => api.get(`/api/v1/sessions?dimension=${dimension}`);
export const getVolumes      = (code, dimension)   => api.get(`/api/v1/volumes/${code}?dimension=${dimension}`);
export const getVolumesSummary = (dimension)       => api.get(`/api/v1/volumes?dimension=${dimension}`);
export const getDbSizes      = ()                  => api.get('/api/v1/dbsizes');
export const getEtlRuns      = ()                  => api.get('/api/v1/etl');

// ── Cost ───────────────────────────────────────────────────────────────────────
export const getCosts        = (dimension)         => api.get(`/api/v1/cost?dimension=${dimension}`);
export const getFullCosts    = (dimension)         => api.get(`/api/v1/cost/full?dimension=${dimension}`);

// ── Users / roles (new) ────────────────────────────────────────────────────────
export const getMe                  = ()           => api.get('/api/v1/users/me');
export const getExternalUsers       = ()           => api.get('/api/v1/users/external');
export const createExternalUser     = (body)       => api.post('/api/v1/users/external', body);
export const updateExternalUser     = (id, body)   => api.patch(`/api/v1/users/external/${id}`, body);
export const deactivateExternalUser = (id)         => api.delete(`/api/v1/users/external/${id}`);
export const getInternalRoles       = ()           => api.get('/api/v1/users/internal-roles');
export const setInternalRole        = (body)       => api.post('/api/v1/users/internal-roles', body);
export const removeInternalRole     = (email)      => api.delete(`/api/v1/users/internal-roles/${encodeURIComponent(email)}`);

export default api;
